import { isJsonObject } from './bytes';
import { assertVerified } from './envelope';
import {
  EXPIRED,
  RECHECK_WINDOW_MS,
  UNREACHABLE,
  clampedLockGraceMs,
  clampedPollIntervalMs,
  deadlinesAfterConfirmation,
  hasGrace,
  pastDeadline,
  requestTimeoutMs,
  retryDelayMs,
  type BeatTiming,
  type Deadlines,
} from './heartbeatDeadlines';
import { Timer } from './heartbeatTimer';
import {
  isDenyBody,
  isFiniteNumber,
  lockGraceSecondsOrZero,
  postJson,
  type HeartbeatRequestBody,
  type SessionHeartbeatBody,
  type SessionHeartbeatPath,
} from './keyApi';

import type { Heartbeat, HeartbeatDeps, KeySession, LockReason, VerifiedEnvelope } from './types';

// setTimeout stalls while the machine sleeps and is throttled in a hidden tab, so the page coming
// back is checked against the wall clock rather than waiting for the next timer.
const RESUME_EVENTS = ['visibilitychange', 'pageshow', 'focus', 'online'] as const;

type HeartbeatOutcome =
  | { kind: 'allow'; timing: Required<BeatTiming>; secondsRemaining: number }
  | { kind: 'deny'; reason: string }
  | { kind: 'failed' };

const FAILED: HeartbeatOutcome = { kind: 'failed' };

interface Suspension {
  sinceWallMs: number;
  failures: number;
}
// Aborting locks rather than just stopping, so nothing protected is ever left up unenforced.
const ABORTED: LockReason = { kind: 'aborted' };

export interface HeartbeatListener {
  onLock: (lock: LockReason) => void;
  onSuspend: () => void;
  onResume: () => void;
  onConfirmed?: (secondsRemaining: number) => void;
}

export function startHeartbeat(
  verified: VerifiedEnvelope,
  session: KeySession,
  deps: HeartbeatDeps,
  listener: HeartbeatListener,
  signal?: AbortSignal,
): Heartbeat {
  assertVerified(verified);
  const body: HeartbeatRequestBody = {
    downloadId: verified.header.downloadId,
    launcherVersion: verified.header.launcherVer,
    jti: session.jti,
  };
  return startSessionHeartbeat('/key/heartbeat', body, session, deps, listener, signal);
}

export function startSessionHeartbeat(
  path: SessionHeartbeatPath,
  body: SessionHeartbeatBody,
  session: KeySession,
  deps: HeartbeatDeps,
  listener: HeartbeatListener,
  signal?: AbortSignal,
): Heartbeat {
  const pageEvents = deps.pageEvents ?? browserWindow();
  const nextPollTimer = new Timer();
  const requestTimeoutTimer = new Timer();
  const unreachableTimer = new Timer();
  const expiryTimer = new Timer();
  const recheckGiveUpTimer = new Timer();
  let timing: BeatTiming = {
    lockGraceMs: clampedLockGraceMs(lockGraceSecondsOrZero(session.lockGraceSeconds)),
  };
  let deadlines: Deadlines;
  let consecutiveFailures = 0;
  let inFlight: { request: AbortController; sentAtWallMs: number } | undefined;
  let suspension: Suspension | undefined;
  let ended = false;

  function stop(): void {
    ended = true;
    signal?.removeEventListener('abort', lockOnAbort);
    for (const type of RESUME_EVENTS) pageEvents?.removeEventListener(type, onPageBack);
    nextPollTimer.clear();
    unreachableTimer.clear();
    expiryTimer.clear();
    recheckGiveUpTimer.clear();
    abandonInFlight();
  }

  // The heartbeat's own state is settled before the route is told, so a route that throws is
  // rethrown outside the heartbeat for error reporting instead of unwinding it.
  function tellRoute(notify: () => void): boolean {
    try {
      notify();
      return true;
    } catch (err) {
      queueMicrotask(() => {
        throw err;
      });
      return false;
    }
  }

  function lock(why: LockReason): void {
    if (ended) {
      return;
    }
    stop();
    tellRoute(() => listener.onLock(why));
  }

  function lockOnUnexpectedError(): void {
    lock(UNREACHABLE);
  }

  function lockOnAbort(): void {
    lock(ABORTED);
  }

  function failClosedOnThrow<Args extends unknown[]>(
    fn: (...args: Args) => void,
  ): (...args: Args) => void {
    return (...args) => {
      try {
        fn(...args);
      } catch {
        lockOnUnexpectedError();
      }
    };
  }

  function suspendAndRecheck(): void {
    suspension = { sinceWallMs: Date.now(), failures: 0 };
    if (!tellRoute(() => listener.onSuspend())) {
      lock(UNREACHABLE);
    }
    if (ended) {
      return;
    }
    setTimerCheckingDeadlines(recheckGiveUpTimer, RECHECK_WINDOW_MS, () => lock(UNREACHABLE));
    beatNow();
  }

  function beatNow(): void {
    nextPollTimer.clear();
    abandonInFlight();
    poll();
  }

  // A stale grant with no failure counted is what sleep or a throttled hidden tab leaves, so the
  // document is hidden and re-checked rather than locked. Returns whether the caller must stop.
  function onUnreachableDeadline(): boolean {
    if (consecutiveFailures > 0) {
      lock(UNREACHABLE);
      return true;
    }
    if (suspension) {
      return false;
    }
    suspendAndRecheck();
    return true;
  }

  // The re-check window is a wall-clock deadline like the others, so an allow or a page event
  // arriving after sleep stalled the give-up timer still locks rather than resumes.
  function deadlineTookOver(): boolean {
    if (ended) {
      return true;
    }
    const now = Date.now();
    const passed = pastDeadline(deadlines, now);
    if (passed?.kind === 'expired') lock(EXPIRED);
    else if (suspension && now - suspension.sinceWallMs >= RECHECK_WINDOW_MS) lock(UNREACHABLE);
    else if (passed?.kind === 'unreachable') return onUnreachableDeadline();
    return ended;
  }

  // Monotonic timers and the wall clock each act at their own deadline, so whichever is earlier
  // wins: sleep cannot hold a lock back, and turning the system clock back cannot extend one.
  function setTimerCheckingDeadlines(timer: Timer, delayMs: number, fn: () => void): void {
    timer.start(
      delayMs,
      failClosedOnThrow(() => {
        if (!deadlineTookOver()) fn();
      }),
    );
  }

  function restartDeadlinesOrLock(secondsRemaining: number): boolean {
    const now = Date.now();
    deadlines = deadlinesAfterConfirmation(now, secondsRemaining, timing);
    setTimerCheckingDeadlines(expiryTimer, deadlines.expiresAtWallMs - now, () => lock(EXPIRED));
    setTimerCheckingDeadlines(unreachableTimer, deadlines.unreachableAtWallMs - now, () => {
      onUnreachableDeadline();
    });
    return deadlineTookOver();
  }

  function abandonInFlight(): void {
    requestTimeoutTimer.clear();
    inFlight?.request.abort();
    inFlight = undefined;
  }

  function inFlightIsFresh(): boolean {
    return inFlight !== undefined && Date.now() - inFlight.sentAtWallMs < requestTimeoutMs(timing);
  }

  function poll(): void {
    const request = new AbortController();
    inFlight = { request, sentAtWallMs: Date.now() };
    setTimerCheckingDeadlines(requestTimeoutTimer, requestTimeoutMs(timing), () => {
      request.abort();
      settle(request, FAILED);
    });
    pollHeartbeat(deps, path, body, request.signal)
      .then((outcome) => settle(request, outcome))
      .catch(lockOnUnexpectedError);
  }

  function settle(request: AbortController, outcome: HeartbeatOutcome): void {
    if (inFlight?.request !== request) {
      return;
    }
    inFlight = undefined;
    requestTimeoutTimer.clear();
    if (outcome.kind === 'deny') lock({ kind: 'refused', reason: outcome.reason });
    else if (outcome.kind === 'allow') continueAfterAllow(outcome);
    else onFailedBeat();
  }

  function continueAfterAllow(allow: Extract<HeartbeatOutcome, { kind: 'allow' }>): void {
    timing = allow.timing;
    consecutiveFailures = 0;
    if (restartDeadlinesOrLock(allow.secondsRemaining)) {
      return;
    }
    setTimerCheckingDeadlines(nextPollTimer, allow.timing.pollIntervalMs, poll);
    tellRoute(() => listener.onConfirmed?.(allow.secondsRemaining));
    if (suspension) {
      suspension = undefined;
      recheckGiveUpTimer.clear();
      tellRoute(() => listener.onResume());
    }
  }

  // The deadline is checked before a failure counts, so a failure that lands after it (a beat in
  // flight across sleep) suspends exactly as a resume event arriving first would.
  function onFailedBeat(): void {
    if (suspension) retryRecheckOrLock(suspension);
    else if (!deadlineTookOver()) retryWithinGraceOrLock();
  }

  function retryWithinGraceOrLock(): void {
    if (!hasGrace(timing)) {
      lock(UNREACHABLE);
      return;
    }
    consecutiveFailures += 1;
    setTimerCheckingDeadlines(nextPollTimer, retryDelayMs(consecutiveFailures), poll);
  }

  function retryRecheckOrLock(current: Suspension): void {
    if (deadlineTookOver()) {
      return;
    }
    current.failures += 1;
    setTimerCheckingDeadlines(nextPollTimer, retryDelayMs(current.failures), poll);
  }

  const onPageBack = failClosedOnThrow((event: Event): void => {
    if (isHiddenDocument(event.target) || deadlineTookOver() || inFlightIsFresh()) {
      return;
    }
    if (suspension && event.type !== 'online') {
      return;
    }
    beatNow();
  });

  if (signal?.aborted) {
    ended = true;
    return { stop };
  }
  signal?.addEventListener('abort', lockOnAbort);
  for (const type of RESUME_EVENTS) pageEvents?.addEventListener(type, onPageBack);
  failClosedOnThrow(() => {
    if (!restartDeadlinesOrLock(session.secondsRemaining)) poll();
  })();
  return { stop };
}

function browserWindow(): EventTarget | undefined {
  return typeof window === 'undefined' ? undefined : window;
}

function isHiddenDocument(target: EventTarget | null): boolean {
  return (target as { visibilityState?: unknown } | null)?.visibilityState === 'hidden';
}

type Settled<T> = { ok: true; value: T } | { ok: false };

function settled<T>(pending: Promise<T>): Promise<Settled<T>> {
  return pending.then(
    (value) => ({ ok: true, value }),
    () => ({ ok: false }),
  );
}

// A rejection is a network or HTTP outcome and counts against the grace. A synchronous throw
// is never how fetch, Response.json or getIdToken report failure, so it escapes and fails closed.
async function pollHeartbeat(
  deps: HeartbeatDeps,
  path: SessionHeartbeatPath,
  body: SessionHeartbeatBody,
  signal: AbortSignal,
): Promise<HeartbeatOutcome> {
  const idToken = await settled(deps.getIdToken());
  if (!idToken.ok || signal.aborted) {
    return FAILED;
  }
  const response = await settled(postJson(deps, path, idToken.value, body, signal));
  if (!response.ok) {
    return FAILED;
  }
  const json = await settled<unknown>(response.value.json());
  const payload = json.ok ? json.value : undefined;
  if (isDenyBody(payload)) {
    return { kind: 'deny', reason: payload.reason };
  }
  if (response.value.status !== 200 || !isJsonObject(payload) || payload.allow !== true) {
    return FAILED;
  }
  const { pollIntervalSeconds, secondsRemaining, lockGraceSeconds } = payload;
  if (
    !isFiniteNumber(pollIntervalSeconds) ||
    pollIntervalSeconds <= 0 ||
    !isFiniteNumber(secondsRemaining) ||
    secondsRemaining < 0
  ) {
    return FAILED;
  }
  return {
    kind: 'allow',
    timing: {
      pollIntervalMs: clampedPollIntervalMs(pollIntervalSeconds),
      lockGraceMs: clampedLockGraceMs(lockGraceSecondsOrZero(lockGraceSeconds)),
    },
    secondsRemaining,
  };
}
